import { 
  collection, 
  addDoc, 
  query, 
  getDocs, 
  orderBy, 
  Timestamp,
  where,
  serverTimestamp,
  doc,
  getDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../Firebase/Config';

export interface Review {
  id?: string;
  productId: string;
  productName: string;
  userId: string;
  userName: string;
  userEmail: string;
  rating: number;
  comment: string;
  imageUrl?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export const submitReview = async (
  reviewData: Omit<Review, 'id' | 'createdAt' | 'updatedAt' | 'productName'>,
  imageFile?: File
): Promise<{ success: boolean; message: string; reviewId?: string }> => {
  try {
    // Fetch product name from Firestore
    const productRef = doc(db, 'products', reviewData.productId);
    const productSnap = await getDoc(productRef);
    
    if (!productSnap.exists()) {
      return {
        success: false,
        message: 'Product not found. Unable to submit review.'
      };
    }
    
    const productName = productSnap.data().name;
    
    let imageUrl = '';

    // Upload image if provided
    if (imageFile) {
      const imageRef = ref(storage, `reviews/${Date.now()}_${imageFile.name}`);
      const snapshot = await uploadBytes(imageRef, imageFile);
      imageUrl = await getDownloadURL(snapshot.ref);
    }

    // Add review to Firestore with product name
    const reviewRef = await addDoc(collection(db, 'reviews'), {
      ...reviewData,
      productName, // Add product name here
      imageUrl,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return {
      success: true,
      message: 'Review submitted successfully!',
      reviewId: reviewRef.id
    };
  } catch (error) {
    console.error('Error submitting review:', error);
    return {
      success: false,
      message: 'Failed to submit review. Please try again.'
    };
  }
};

export const fetchProductReviews = async (productId: string): Promise<Review[]> => {
  try {
    const reviewsRef = collection(db, 'reviews');
    const q = query(
      reviewsRef,
      where('productId', '==', productId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const reviews: Review[] = [];

    snapshot.forEach(doc => {
      reviews.push({
        id: doc.id,
        ...doc.data()
      } as Review);
    });

    return reviews;
  } catch (error) {
    console.error('Error fetching reviews:', error);
    return [];
  }
};